import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { ROLE_QUERY_KEYS } from './keys';

import * as permissionsService from 'services/permission';
import * as rolesService from 'services/role';

import { handleError } from 'utils/errorHandler';

export type PermissionItem = {
  id: number;
  label: string;
};

export type RolePermissionRecord = {
  id: number;
  permissions: PermissionItem[];
};

type ToggleRolePermissionPayload = {
  roleId: number;
  permission: PermissionItem;
  shouldRevoke: boolean;
};

type ToggleRolePermissionContext = {
  previousRolesPermissions: RolePermissionRecord[];
};

const EMPTY_ROLES_PERMISSIONS: RolePermissionRecord[] = [];

export const usePermissionsQuery = (): UseQueryResult<PermissionItem[], Error> => {
  return useQuery<PermissionItem[], Error>({
    queryKey: [ROLE_QUERY_KEYS.PERMISSIONS],
    queryFn: () => permissionsService.fetchAll<PermissionItem[]>(),
  });
};

export const useRolePermissionsQuery = (): UseQueryResult<RolePermissionRecord[], Error> => {
  return useQuery<RolePermissionRecord[], Error>({
    queryKey: [ROLE_QUERY_KEYS.ROLES_PERMISSIONS],
    queryFn: () => rolesService.fetchRolesPermissions<RolePermissionRecord[]>(),
    placeholderData: EMPTY_ROLES_PERMISSIONS,
    staleTime: Infinity,
  });
};

export const useToggleRolePermissionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, ToggleRolePermissionPayload, ToggleRolePermissionContext>({
    mutationFn: async ({ roleId, permission, shouldRevoke }) => {
      if (shouldRevoke) {
        await rolesService.revokeRolesPermissions(roleId, permission.id);

        return;
      }

      await rolesService.grantRolesPermissions(roleId, permission.id);
    },
    onMutate: async ({ roleId, permission, shouldRevoke }) => {
      await queryClient.cancelQueries({ queryKey: [ROLE_QUERY_KEYS.ROLES_PERMISSIONS] });

      const previousRolesPermissions =
        queryClient.getQueryData<RolePermissionRecord[]>([ROLE_QUERY_KEYS.ROLES_PERMISSIONS]) ||
        EMPTY_ROLES_PERMISSIONS;

      queryClient.setQueryData<RolePermissionRecord[]>(
        [ROLE_QUERY_KEYS.ROLES_PERMISSIONS],
        (currentState = EMPTY_ROLES_PERMISSIONS) => {
          return currentState.map((rolePermission) => {
            if (rolePermission.id !== roleId) {
              return rolePermission;
            }

            const nextPermissions = shouldRevoke
              ? rolePermission.permissions.filter((currentPermission) => currentPermission.id !== permission.id)
              : [...rolePermission.permissions, permission];

            return {
              ...rolePermission,
              permissions: nextPermissions,
            };
          });
        }
      );

      return { previousRolesPermissions };
    },
    onError: (error, _variables, context) => {
      if (context?.previousRolesPermissions) {
        queryClient.setQueryData([ROLE_QUERY_KEYS.ROLES_PERMISSIONS], context.previousRolesPermissions);
      }

      handleError(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ROLE_QUERY_KEYS.ROLES_PERMISSIONS] });
    },
  });
};
